import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Box, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import DataTable from 'react-data-table-component'
import DeleteSignupDialog from '../components/PartnerSignups/DeleteSignup'
import FeeApprovals from '../components/PartnerSignups/FeeApprovals'
import { GET_PARTNER_SIGNUPS, formatDate } from '../components/PartnerSignups/queries'
import { Icon, T } from '../components/PartnerSignups/onboardingDesign'
import { Badge, Card, OnboardingPage, PageHeader, PrimaryButton, StatCard, StatusBadge, tableStyles } from '../components/PartnerSignups/onboardingUi'

// An application's pricing, when it isn't simply the standard.
const PRICING = {
  CUSTOM: { label: 'Own fees', kind: 'amber' },
  PENDING: { label: 'Fees waiting for approval', kind: 'amber' },
  REJECTED: { label: 'Fees not approved, not sent', kind: 'red' },
}

const PAGE = '/super_admin/partner-application'

// The sign-up form's step names (rushmyfood-web screens/PartnerSignup).
const STEP_LABELS = {
  business: 'Basic info', legal: 'Legal entity', processing: 'Card processing', owners: 'Owners',
  bank: 'Payout bank', documents: 'Documents', terms: 'Fees & terms',
}

/** How far a draft has got: done of the form's steps, and what is next. */
function Progress({ p }) {
  const ready = !p.next
  return (
    <Box sx={{ mt: 0.75, width: 124 }}>
      <Box sx={{ height: 5, borderRadius: 3, background: T.fill, overflow: 'hidden' }}>
        <Box sx={{ width: `${(p.done / p.total) * 100}%`, height: '100%', borderRadius: 3, background: ready ? T.green : T.red }} />
      </Box>
      <Typography sx={{ color: ready ? T.green : T.text2, fontSize: 12.5, fontWeight: 600, mt: 0.5, lineHeight: 1.35 }}>
        {ready ? 'Ready to sign' : `${p.done} of ${p.total} steps done`}
      </Typography>
      {!ready && <Typography sx={{ color: T.hint, fontSize: 12, lineHeight: 1.35 }}>Next: {STEP_LABELS[p.next] || p.next}</Typography>}
    </Box>
  )
}

function DateLine({ label, value }) {
  return (
    <Box>
      <Typography sx={{ color: T.hint, fontSize: 12, lineHeight: 1.3 }}>{label}</Typography>
      <Typography sx={{ color: T.text2, fontSize: 13, lineHeight: 1.35, whiteSpace: 'nowrap' }}>{formatDate(value)}</Typography>
    </Box>
  )
}

export default function PartnerSignups() {
  const history = useHistory()
  const [search, setSearch] = useState('')
  // The signup being asked about before it is deleted.
  const [deleting, setDeleting] = useState(null)
  // A signup's page: the sign-up form itself beside the admin side.
  const open = (r) => history.push(`${PAGE}/${r._id}`)
  // A new application, filled in in the same form as staff.
  const startApplication = () => history.push(`${PAGE}/new`)

  const { data, loading, refetch } = useQuery(GET_PARTNER_SIGNUPS, { fetchPolicy: 'network-only' })

  const rows = data?.partnerSignups ?? []

  const filtered = search
    ? rows.filter(r =>
        [r.restaurantName, r.contactName, r.restaurantEmail, r.restaurantPhone, r.corporateName, r.status, r.agentName]
          .some(v => v && v.toLowerCase().includes(search.toLowerCase()))
      )
    : rows

  const columns = [
    {
      name: 'Date', hide: 'md',
      cell: r => (
        <Box sx={{ py: 1.25, display: 'grid', gap: 0.75 }}>
          <DateLine label="Started" value={r.createdAt} />
          <DateLine label="Last updated" value={r.updatedAt || r.createdAt} />
        </Box>
      ),
      width: '190px',
    },
    {
      name: 'Restaurant',
      cell: r => (
        <Box sx={{ py: 1.25 }}>
          <Typography sx={{ fontWeight: 600, color: T.text, fontSize: 15 }}>{r.restaurantName}</Typography>
          {(r.businessType === 'GROCERY' || r.agentName || r.startedByName) && (
            <Typography sx={{ color: T.hint, fontSize: 13, display: 'block' }}>
              {[
                r.businessType === 'GROCERY' ? 'Grocery' : null,
                r.startedByName ? `started by ${r.startedByName}` : r.agentName ? `via ${r.agentName}` : null,
              ].filter(Boolean).join(' · ')}
            </Typography>
          )}
          {PRICING[r.pricingStatus] && (
            <Badge kind={PRICING[r.pricingStatus].kind} sx={{ mt: 0.5, fontSize: 11.5 }}>{PRICING[r.pricingStatus].label}</Badge>
          )}
        </Box>
      ),
      grow: 1,
    },
    {
      name: 'Contact', hide: 'md',
      cell: r => (
        <Box>
          <Typography sx={{ fontWeight: 500, fontSize: 14, color: T.text }}>{r.contactName}</Typography>
          <Typography sx={{ color: T.hint, fontSize: 13 }}>{r.contactPhone}</Typography>
        </Box>
      ),
      width: '180px',
    },
    {
      name: 'Email', hide: 'sm',
      cell: r => <Typography sx={{ color: T.text2, fontSize: 13.5, overflowWrap: 'anywhere' }}>{r.restaurantEmail}</Typography>,
      width: '220px',
    },
    {
      name: 'POS?', hide: 'sm',
      cell: r => (r.interestedInPos
        ? <Badge kind="green">Yes</Badge>
        : <Typography sx={{ color: T.hint, fontSize: 13 }}>No</Typography>),
      width: '80px',
      center: true,
    },
    {
      name: 'Status',
      cell: r => (
        <Box sx={{ py: 1.25 }}>
          <StatusBadge status={r.status} />
          {r.progress && <Progress p={r.progress} />}
          {r.signedAt && (
            <Typography sx={{ display: 'block', color: T.hint, fontSize: 12.5, mt: 0.5 }}>Signed {formatDate(r.signedAt)}</Typography>
          )}
        </Box>
      ),
      width: '150px',
    },
    {
      name: '',
      cell: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Only ones never signed or onboarded (canDelete). */}
          {r.canDelete && (
            <IconButton
              aria-label={`Delete ${r.restaurantName}`}
              onClick={(e) => { e.stopPropagation(); setDeleting(r) }}
              sx={{ color: T.hint, width: 40, height: 40, '&:hover': { background: T.redTint, color: T.red } }}
            >
              <Icon.Trash size={17} />
            </IconButton>
          )}
          <IconButton aria-label={`Open ${r.restaurantName}`} onClick={() => open(r)} sx={{ color: T.link, width: 40, height: 40, '&:hover': { background: T.redTint } }}>
            <Icon.ArrowRight size={18} />
          </IconButton>
        </Box>
      ),
      width: '108px',
      right: true,
    },
  ]

  return (
    <OnboardingPage>
      <PageHeader
        title="Partner Signups"
        subtitle="Restaurant signup submissions from the marketing site"
        actions={(
          <>
            <PrimaryButton onClick={startApplication}>
              <Icon.Plus size={18} />
              Start an application
            </PrimaryButton>
            <IconButton aria-label="Reload" onClick={() => refetch()} sx={{ width: 42, height: 42, border: `1px solid ${T.input}`, color: T.muted, '&:hover': { background: T.fill } }}>
              <Icon.Refresh size={18} />
            </IconButton>
          </>
        )}
      />

      <FeeApprovals onChanged={() => refetch()} />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 2, mb: 3 }}>
        {[
          { label: 'Total', value: rows.length, tone: 'text' },
          { label: 'Signed, awaiting approval', value: rows.filter(r => r.status === 'SIGNED').length, tone: 'purple' },
          { label: 'New', value: rows.filter(r => r.status === 'NEW').length, tone: 'info' },
          { label: 'Onboarded', value: rows.filter(r => r.status === 'ONBOARDED').length, tone: 'green' },
          { label: 'Interested in POS', value: rows.filter(r => r.interestedInPos).length, tone: 'red' },
        ].map(s => <StatCard key={s.label} value={s.value} label={s.label} tone={s.tone} />)}
      </Box>

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 2, sm: 2.5 }, borderBottom: `1px solid ${T.divider}` }}>
          <TextField
            placeholder="Search by restaurant, contact, email, status..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            inputProps={{ 'aria-label': 'Search partner signups' }}
            sx={{ width: { xs: '100%', sm: 420 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Box sx={{ color: T.placeholder, display: 'flex' }}><Icon.Search size={18} /></Box></InputAdornment> }}
          />
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: T.red }} />
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            pagination
            highlightOnHover
            pointerOnHover
            onRowClicked={open}
            customStyles={tableStyles}
            noDataComponent={
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ color: T.hint }}>No partner signups yet.</Typography>
              </Box>
            }
          />
        )}
      </Card>

      <DeleteSignupDialog
        row={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => { setDeleting(null); refetch() }}
      />
    </OnboardingPage>
  )
}
